import { useSnackbarController, useReferenceDialog } from "@firecms/core";
import { Button, CircularProgress } from "@firecms/ui";
import { httpsCallable } from "firebase/functions";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AdminModal } from "../../custom/AdminModal";
import { buildTeamsCollection } from "../../collections/teams";
import { useFirebaseFunctions } from "../../hooks/useFirebaseFunctions";
import { useAdminTranslation } from "../../i18n/useAdminTranslation";
import { getEventPath } from "../../utils";
import { getModeCollectionIds } from "../../workspace/adminMode";
import { useAdminWorkspace } from "../../workspace/useAdminWorkspace";
import { registerOpenApproveStandbyDialog } from "./approveStandbyBridge";

export function ApproveStandbyDialogHost() {
  const userIdRef = useRef<string | null>(null);
  const loadingRef = useRef(false);
  const [choiceOpen, setChoiceOpen] = useState(false);
  const [standbyReason, setStandbyReason] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const { mode } = useAdminWorkspace();
  const eventId = useMemo(
    () => getModeCollectionIds(mode).event ?? null,
    [mode]
  );
  const functions = useFirebaseFunctions();
  const snackbarController = useSnackbarController();
  const { t: ta } = useAdminTranslation();

  const callApprove = useCallback(
    async (teamId?: string) => {
      const currentUserId = userIdRef.current;
      if (!functions || !eventId || !currentUserId) {
        return false;
      }

      const approve = httpsCallable(functions, "approveStandbyParticipant");
      try {
        loadingRef.current = true;
        setLoading(true);
        await approve({
          accountId: eventId.accountId,
          eventId: eventId.eventId,
          userId: currentUserId,
          ...(teamId ? { teamId } : {}),
        });
        snackbarController.open({
          message: ta("approveStandby.success"),
          type: "success",
        });
        userIdRef.current = null;
        return true;
      } catch (error: unknown) {
        const message =
          error instanceof Error ? error.message : ta("common.unknownError");
        snackbarController.open({
          message: ta("approveStandby.error", { message }),
          type: "error",
        });
        console.error(error);
        return false;
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [eventId, functions, snackbarController, ta]
  );

  const assignTeam = useCallback(
    async (teamEntity: { id: string } | null) => {
      if (!teamEntity) {
        userIdRef.current = null;
        return;
      }
      const ok = await callApprove(teamEntity.id);
      if (!ok) {
        // Keep userId so the manager can reopen Approve and retry.
        return;
      }
    },
    [callApprove]
  );

  const referenceDialog = useReferenceDialog({
    path: eventId ? getEventPath(eventId) + "/teams" : "teams",
    collection: eventId
      ? buildTeamsCollection(eventId)
      : buildTeamsCollection({
          accountId: "",
          spaceId: "",
          eventId: "",
        }),
    multiselect: false,
    onSingleEntitySelected: assignTeam,
  });

  const openReferenceDialogRef = useRef(referenceDialog.open);
  openReferenceDialogRef.current = referenceDialog.open;

  const closeChoice = useCallback(() => {
    if (loading) {
      return;
    }
    setChoiceOpen(false);
    setStandbyReason(undefined);
    userIdRef.current = null;
  }, [loading]);

  useEffect(() => {
    return registerOpenApproveStandbyDialog((id, reason) => {
      if (!eventId) {
        snackbarController.open({
          message: ta("common.selectActiveEvent"),
          type: "warning",
        });
        return;
      }
      if (loadingRef.current) {
        return;
      }
      userIdRef.current = id;
      setStandbyReason(reason);
      setChoiceOpen(true);
    });
  }, [eventId, snackbarController, ta]);

  return (
    <AdminModal
      open={choiceOpen}
      onClose={closeChoice}
      ariaLabel={ta("approveStandby.ariaLabel")}
      title={ta("approveStandby.title")}
      description={
        standbyReason === "priorJoin"
          ? `${ta("approveStandby.priorJoinBody")}\n\n${ta("approveStandby.body")}`
          : ta("approveStandby.body")
      }
      footer={
        <>
          <Button onClick={closeChoice} disabled={loading} variant="outlined">
            {ta("approveStandby.cancel")}
          </Button>
          <Button
            onClick={() => {
              setChoiceOpen(false);
              openReferenceDialogRef.current();
            }}
            disabled={loading}
            variant="outlined"
          >
            {ta("approveStandby.chooseTeam")}
          </Button>
          <Button
            onClick={async () => {
              const ok = await callApprove();
              if (ok) {
                setChoiceOpen(false);
              }
            }}
            disabled={loading}
            color="primary"
          >
            {loading ? (
              <CircularProgress size="smallest" />
            ) : (
              ta("approveStandby.autoMatch")
            )}
          </Button>
        </>
      }
    />
  );
}
